import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { itAssetsAPI } from '../../utils/api';

const DEFAULT_DOMAIN = 'IT_ASSETS';

const normalizeKey = (value) => String(value || '').trim().toLowerCase();

const LookupManagement = () => {
  const [domain, setDomain] = useState(DEFAULT_DOMAIN);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [fieldKey, setFieldKey] = useState('');
  const [lookups, setLookups] = useState({});
  const [rows, setRows] = useState([]);

  const [showAdd, setShowAdd] = useState(false);
  const [newValue, setNewValue] = useState('');

  const [confirm, setConfirm] = useState({ show: false, type: '', row: null });

  const fieldKeys = useMemo(() => Object.keys(lookups || {}).sort((a, b) => a.localeCompare(b)), [lookups]);

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const loadLookups = useCallback(async () => {
    resetMessages();
    setLoading(true);
    try {
      const res = await itAssetsAPI.getLookups(domain);
      const data = (res && typeof res === 'object' && 'success' in res && res.data) ? res.data : (res?.data?.data || res?.data || {});
      setLookups(data);
      const nextFieldKey = fieldKey && data?.[fieldKey] ? fieldKey : (Object.keys(data || {})[0] || '');
      setFieldKey(nextFieldKey);
    } catch (e) {
      const backendPath = e?.data?.path || e?.details?.data?.path;
      const msg = e?.response?.data?.message || e?.message || 'Failed to load lookups.';
      setError(backendPath ? `${msg} (${backendPath})` : msg);
      setLookups({});
      setFieldKey('');
    } finally {
      setLoading(false);
    }
  }, [domain, fieldKey]);

  const loadOptions = useCallback(async () => {
    if (!domain || !fieldKey) {
      setRows([]);
      return;
    }
    resetMessages();
    setLoading(true);
    try {
      const res = await itAssetsAPI.getLookupOptions(domain, fieldKey, true);
      const data = (res && typeof res === 'object' && 'success' in res && res.data) ? res.data : (res?.data?.data || res?.data || []);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const backendPath = e?.data?.path || e?.details?.data?.path;
      const msg = e?.response?.data?.message || e?.message || 'Failed to load lookup options.';
      setError(backendPath ? `${msg} (${backendPath})` : msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [domain, fieldKey]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const onAdd = async () => {
    resetMessages();
    try {
      await itAssetsAPI.createLookupOption({ domain, fieldKey, value: newValue, isActive: true });
      setSuccess('Option added.');
      setNewValue('');
      setShowAdd(false);
      await loadLookups();
      await loadOptions();
      globalThis.dispatchEvent(new Event('lookupsChanged'));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to add option.');
    }
  };

  const onToggleActive = async (row) => {
    resetMessages();
    try {
      await itAssetsAPI.updateLookupOption(row.id, { isActive: !row.is_active });
      setSuccess(`Option ${row.is_active ? 'deactivated' : 'activated'}.`);
      await loadLookups();
      await loadOptions();
      globalThis.dispatchEvent(new Event('lookupsChanged'));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update option.');
    }
  };

  const onChangeValue = async (row, value) => {
    resetMessages();
    try {
      await itAssetsAPI.updateLookupOption(row.id, { value });
      setSuccess('Option updated.');
      await loadLookups();
      await loadOptions();
      globalThis.dispatchEvent(new Event('lookupsChanged'));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update option.');
    }
  };

  const onDeactivate = async (row) => {
    // Deleting a lookup option also clears that value from existing assets/allocation rows.
    // This is intentional per business requirement.
    resetMessages();
    try {
      await itAssetsAPI.deleteLookupOption(row.id);
      setSuccess('Option deleted and existing data cleared.');
      await loadLookups();
      await loadOptions();
      globalThis.dispatchEvent(new Event('lookupsChanged'));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete option.');
    }
  };

  const requestToggleActive = (row) => {
    setConfirm({ show: true, type: 'toggle', row });
  };

  const requestDelete = (row) => {
    setConfirm({ show: true, type: 'delete', row });
  };

  const runConfirmed = async () => {
    const row = confirm.row;
    const type = confirm.type;
    setConfirm({ show: false, type: '', row: null });
    if (!row) return;
    if (type === 'toggle') return onToggleActive(row);
    if (type === 'delete') return onDeactivate(row);
  };

  const activeCount = useMemo(() => rows.filter((r) => r.is_active).length, [rows]);

  return (
    <Card className="shadow-sm border-0">
      <Card.Body>
        <Row className="align-items-center mb-3">
          <Col>
            <h4 className="mb-0">Master Dropdowns</h4>
            <div className="text-muted small">Manage dropdown values stored in DB (Admin only).</div>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => loadLookups()}>
              <FiRefreshCw className="me-1" /> Refresh
            </Button>
            <Button variant="primary" onClick={() => setShowAdd(true)} disabled={!fieldKey}>
              <FiPlus className="me-1" /> Add Option
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

        <Row className="g-2 mb-3">
          <Col md={3}>
            <Form.Label>Domain</Form.Label>
            <Form.Select value={domain} onChange={(e) => setDomain(e.target.value)}>
              <option value="IT_ASSETS">IT Assets</option>
            </Form.Select>
          </Col>
          <Col md={5}>
            <Form.Label>Field</Form.Label>
            <Form.Select value={fieldKey} onChange={(e) => setFieldKey(e.target.value)}>
              {fieldKeys.length === 0 && <option value="">No fields</option>}
              {fieldKeys.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4} className="d-flex align-items-end justify-content-md-end">
            <div className="text-muted small">
              Options: <span className="fw-semibold">{rows.length}</span> · Active: <span className="fw-semibold">{activeCount}</span>
            </div>
          </Col>
        </Row>

        <Card className="border-0 bg-light-subtle">
          <Card.Body>
            {loading ? (
              <div className="py-4 d-flex justify-content-center">
                <Spinner animation="border" />
              </div>
            ) : (
              <div className="table-responsive">
	                <Table hover size="sm" className="mb-0 align-middle">
	                  <thead>
	                    <tr>
	                      <th style={{ width: 90 }}>Status</th>
	                      <th>Value</th>
	                      <th style={{ width: 180 }} className="text-end">Actions</th>
	                    </tr>
	                  </thead>
	                  <tbody>
	                    {rows.length === 0 && (
	                      <tr>
	                        <td colSpan={3} className="text-center text-muted py-4">
	                          No options found.
	                        </td>
	                      </tr>
	                    )}
	                    {rows.map((r) => (
	                      <LookupRow
	                        key={r.id}
	                        row={r}
	                        onToggleActive={requestToggleActive}
	                        onChangeValue={onChangeValue}
	                        onDeactivate={requestDelete}
	                      />
	                    ))}
	                  </tbody>
	                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Card.Body>

      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Option</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-2">
            Domain: <span className="fw-semibold">{domain}</span> · Field: <span className="fw-semibold">{fieldKey}</span>
          </div>
          <Form.Label>Value *</Form.Label>
          <Form.Control value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="Enter option value" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button variant="primary" onClick={onAdd} disabled={!String(newValue || '').trim()}>
            Add
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirm.show} onHide={() => setConfirm({ show: false, type: '', row: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirm.type === 'delete' ? 'Delete Option' : (confirm.row?.is_active ? 'Deactivate Option' : 'Activate Option')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-2">
            Field: <span className="fw-semibold">{fieldKey || '-'}</span>
          </div>
          <div className="mb-3">
            Value: <span className="fw-semibold">{confirm.row?.value || '-'}</span>
          </div>
          {confirm.type === 'delete' ? (
            <Alert variant="warning" className="mb-0">
              Deleting this option will also clear this value from existing assets/allocations. This cannot be undone.
            </Alert>
          ) : (
            <Alert variant="info" className="mb-0">
              {confirm.row?.is_active ? 'Deactivated options will stop appearing in dropdowns.' : 'Activated options will appear in dropdowns.'}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirm({ show: false, type: '', row: null })}>Cancel</Button>
          <Button variant={confirm.type === 'delete' ? 'danger' : 'primary'} onClick={runConfirmed}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

const LookupRow = ({ row, onToggleActive, onChangeValue, onDeactivate }) => {
  const [value, setValue] = useState(row.value);

  useEffect(() => {
    setValue(row.value);
  }, [row.value]);

  const isDirtyValue = normalizeKey(value) !== normalizeKey(row.value);

  return (
    <tr>
      <td>
        <Badge bg={row.is_active ? 'success' : 'secondary'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
      </td>
      <td>
        <Form.Control
          size="sm"
          value={value}
          disabled={!row.is_active}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => {
            if (row.is_active && isDirtyValue && String(value || '').trim()) onChangeValue(row, value);
          }}
        />
      </td>
      <td className="text-end">
        <div className="d-flex justify-content-end gap-2">
          <Button size="sm" variant={row.is_active ? 'outline-secondary' : 'outline-success'} onClick={() => onToggleActive(row)}>
            {row.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Button size="sm" variant="outline-danger" onClick={() => onDeactivate(row)} title="Deactivate (soft delete)">
            <FiTrash2 />
          </Button>
        </div>
      </td>
    </tr>
  );
};

LookupRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.number.isRequired,
    value: PropTypes.string,
    is_active: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired
  }).isRequired,
  onToggleActive: PropTypes.func.isRequired,
  onChangeValue: PropTypes.func.isRequired,
  onDeactivate: PropTypes.func.isRequired
};

export default LookupManagement;
